import * as React from 'react'
import {connect} from 'react-redux'

import {CollapsibleBlock} from '../../../actions/collapsibleBlockTypes'
import type {State} from '../../../reducers/types'
import {getAgentsArg} from '../../../rest/agents'
import {agentsProjectsArg} from '../../../rest/projects'
import {getSorting, getBlocks} from '../../../selectors'
import {restApi} from '../../../services/rest'
import {extractError} from '../../../utils/extractError'
import {notNull} from '../../../utils/guards'

import Agents from './Agents'
import AgentsFetcher from './Agents.fetcher'
import type {ContainerProps, StateProps} from './Agents.types'

const mapStateToProps = (state: State): StateProps => ({
  collapsedPools: getBlocks(state, CollapsibleBlock.COLLAPSED_AGENT_POOLS),
  sorting: getSorting(state),
})

function AgentsContainer({
  locator,
  updatePeriod,
  update$,
  withFetcher = true,
  additionalFields,
  children,
  pause,
  resetLocatorState,
  ...restProps
}: ContainerProps) {
  const {normalizedData, ready, agentsError} = restApi.endpoints.getAllAgentsNormalized.useQuery(
    getAgentsArg(locator, additionalFields),
    {
      selectFromResult: ({data, error}) => ({
        normalizedData: data,
        ready: data != null,
        agentsError: error,
      }),
    },
  )
  const {projectsReady, projectsError} = restApi.endpoints.getAllProjectsNormalized.useQuery(
    agentsProjectsArg,
    {selectFromResult: ({data, error}) => ({projectsReady: data != null, projectsError: error})},
  )
  const data = React.useMemo(
    () =>
      normalizedData?.result.map(id => normalizedData.entities.agents?.[id]).filter(notNull) ?? [],
    [normalizedData],
  )
  return (
    <>
      {withFetcher && (
        <AgentsFetcher
          locator={locator}
          updatePeriod={updatePeriod}
          update$={update$}
          additionalFields={additionalFields}
          pause={pause}
          resetLocatorState={resetLocatorState}
        >
          {children}
        </AgentsFetcher>
      )}
      <Agents
        {...restProps}
        data={data}
        ready={ready && projectsReady}
        error={[extractError(agentsError), extractError(projectsError)].filter(Boolean).join('; ')}
      />
    </>
  )
}

export default connect(mapStateToProps)(AgentsContainer)
